import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import * as THREE from 'three';
import { isNestedGlbAssetPivot } from '../../../components/gridpagefunction/MeshExtractation';
import {
  MAP_BACKUP_USERDATA_KEY,
  MAP_VISIBILITY_USERDATA_KEY,
  MAP_PREVIEW_URLS_USERDATA_KEY,
  embedNonGltfMapUrlsInMaterials,
  isPublishableTextureUrl,
  isRestorableTextureUrl,
} from '../../../utils/ThreeJsUtils/materialMaps';
import { withRenderViewStrippedAsync } from '../../../components/gridpagefunction/materialFunction';
import {
  EDITOR_MAP_SCALARS_KEY,
  snapshotMapScalars,
} from '../../../utils/ThreeJsUtils/materialPanelSchema';
const TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'alphaMap',
  'bumpMap',
  'displacementMap',
  'lightMap',
  'specularMap',
  'envMap',
  'matcap',
  'gradientMap',
];

/** VideoTexture / HTMLVideoElement cannot be embedded by GLTFExporter. */
function isNonExportableTexture(tex) {
  if (!tex?.isTexture) return false;
  if (tex.isVideoTexture) return true;
  const img = tex.image;
  if (!img) return false;
  if (typeof HTMLVideoElement !== 'undefined' && img instanceof HTMLVideoElement) {
    return true;
  }
  // Some runtimes expose video frames without a proper ImageBitmap/canvas type.
  if (typeof img.tagName === 'string' && img.tagName.toUpperCase() === 'VIDEO') {
    return true;
  }
  return false;
}

/**
 * True when the user materially edited the asset (not just default API metallic stub).
 */
export function hasMapVisibilityEditsInHierarchy(root) {
  if (!root || typeof root.traverse !== 'function') return false;
  let edited = false;
  root.traverse((obj) => {
    if (!obj.isMesh && !obj.isSkinnedMesh) return;
    const materials = Array.isArray(obj.material)
      ? obj.material
      : [obj.material];
    materials.forEach((mat) => {
      const vis = mat?.userData?.[MAP_VISIBILITY_USERDATA_KEY];
      if (vis && Object.values(vis).some((v) => v === false)) edited = true;
    });
  });
  return edited;
}

export function hasThreeMaterialEditsInHierarchy(root) {
  if (!root || typeof root.traverse !== 'function') {
    return !!root?.userData?.threeMaterialEdited;
  }
  if (root.userData?.threeMaterialEdited) return true;
  let edited = false;
  root.traverse((obj) => {
    if (obj.userData?.threeMaterialEdited) edited = true;
  });
  return edited;
}

/** True when any mesh/skinnedMesh currently has wireframe enabled. */
export function hasWireframeInHierarchy(root) {
  if (!root || typeof root.traverse !== 'function') return false;
  let found = false;
  root.traverse((obj) => {
    if ((!obj.isMesh && !obj.isSkinnedMesh) || !obj.material) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    if (mats.some((m) => m?.wireframe === true)) found = true;
  });
  return found;
}

const DEFAULT_PUBLISH_MATERIAL_TYPE = 'MeshStandardMaterial';

/** True when stored settings include non-default surface flags (side, transparency, opacity). */
export function hasStoredSurfaceMaterialEdits(ms) {
  if (!ms || typeof ms !== 'object') return false;
  if (ms.side && String(ms.side) !== 'Front') return true;
  const t = ms.transparency;
  if (t === true || t === 'Alpha' || t === 'Blend' || t === 'alpha' || t === 'blend') {
    return true;
  }
  const op = ms.opacity ?? ms.alpha;
  if (op !== undefined && Number(op) < 1) return true;
  if (ms.blending && ms.blending !== 'Normal') return true;
  return false;
}

function hasNonDefaultLiveMaterialsInHierarchy(root) {
  if (!root || typeof root.traverse !== 'function') return false;
  let found = false;
  root.traverse((obj) => {
    if (!obj.isMesh && !obj.isSkinnedMesh) return;
    const materials = Array.isArray(obj.material)
      ? obj.material
      : [obj.material];
    materials.forEach((mat) => {
      if (
        mat?.type &&
        mat.type !== DEFAULT_PUBLISH_MATERIAL_TYPE &&
        mat.type !== 'MeshPhysicalMaterial'
      ) {
        found = true;
      }
    });
  });
  return found;
}

export function hasMaterialModifications(groupOrSettings) {
  if (groupOrSettings && typeof groupOrSettings.traverse === 'function') {
    if (hasThreeMaterialEditsInHierarchy(groupOrSettings)) return true;
    if (hasMapVisibilityEditsInHierarchy(groupOrSettings)) return true;
    if (hasNonDefaultLiveMaterialsInHierarchy(groupOrSettings)) return true;
  }
  if (groupOrSettings?.userData?.threeMaterialEdited) return true;

  const ms =
    groupOrSettings?.userData?.material_settings ?? groupOrSettings ?? null;
  if (!ms || typeof ms !== 'object') return false;

  const materialType = ms.materialType || ms.type;
  if (
    materialType &&
    materialType !== DEFAULT_PUBLISH_MATERIAL_TYPE &&
    materialType !== 'MeshPhysicalMaterial'
  ) {
    return true;
  }

  if (ms.shader && ms.shader !== 'Standard' && ms.shader !== 'PBR') return true;
  if (ms.textureImage && String(ms.textureImage).trim()) return true;
  if (ms.renderingMode && ms.renderingMode !== 'Opaque') return true;
  if (ms.mirror) return true;
  if (ms.normal_map && ms.normal_map !== 'None' && String(ms.normal_map).trim())
    return true;

  const albedo = String(ms.albedo || '').toUpperCase();
  if (
    albedo &&
    albedo !== '#FFFFFF' &&
    albedo !== '#00FFFFFF' &&
    albedo !== '#FFFFFFFF'
  ) {
    return true;
  }

  const metallic = ms.metallic;
  if (metallic?.map && String(metallic.map).trim()) return true;

  if (ms.maps && typeof ms.maps === 'object' && Object.keys(ms.maps).length > 0) {
    return true;
  }
  if (
    ms.mapVisibility &&
    typeof ms.mapVisibility === 'object' &&
    Object.keys(ms.mapVisibility).length > 0
  ) {
    return true;
  }
  if (
    ms.mapPreviewUrls &&
    typeof ms.mapPreviewUrls === 'object' &&
    Object.keys(ms.mapPreviewUrls).length > 0
  ) {
    return true;
  }
  // renderView is view-only editor metadata — never trigger GLB rebuild.

  if (hasStoredSurfaceMaterialEdits(ms)) return true;

  if (
    ms.childOverrides &&
    typeof ms.childOverrides === 'object' &&
    Object.keys(ms.childOverrides).length > 0
  ) {
    return true;
  }
  if (
    typeof ms.child_material_data === 'string' &&
    ms.child_material_data.trim().length > 2
  ) {
    return true;
  }
  if (
    typeof ms.wireframe_mesh_indices === 'string' &&
    ms.wireframe_mesh_indices.trim().length > 0
  ) {
    return true;
  }

  return false;
}

/** When true, publish keeps the existing server GLB path (no binary re-upload). */
export const SKIP_GLB_FILE_UPLOAD = Symbol('SKIP_GLB_FILE_UPLOAD');

export function hasSkinnedMeshInHierarchy(root) {
  if (!root || typeof root.traverse !== 'function') return false;
  if (root.userData?.hasSkinnedMesh === true) return true;
  let found = false;
  root.traverse((obj) => {
    if (obj.isSkinnedMesh) found = true;
  });
  return found;
}

export function findOriginalGlbFile(mesh, groupp) {
  const candidates = [
    groupp?.originalFile,
    groupp?.localFile,
    groupp?.userData?.duplicateSourceFile,
    mesh?.originalFile,
    mesh?.localFile,
    mesh?.userData?.duplicateSourceFile,
    mesh?.glb_file,
    mesh?.objPath,
    groupp?.objPath,
  ];
  for (const file of candidates) {
    if (file instanceof File && file.size > 0) return file;
  }
  return null;
}

/** True when this scene/Redux asset was created via duplicate (not API reload). */
export function isDuplicateSceneAsset(mesh, groupp) {
  return [mesh, groupp, mesh?.userData, groupp?.userData].some(
    (entry) => entry?.isDuplicate === true
  );
}

/**
 * Duplicates must not inherit the source's published CDN path.
 * Keep a local File handle so skinned re-duplicate / republish still works
 * before the user has published the copy.
 */
export function isolateDuplicatePublishIdentity(duplicateMesh) {
  if (!duplicateMesh) return;

  const sourceFile =
    (duplicateMesh.originalFile instanceof File &&
      duplicateMesh.originalFile) ||
    (duplicateMesh.localFile instanceof File && duplicateMesh.localFile) ||
    (duplicateMesh.userData?.duplicateSourceFile instanceof File &&
      duplicateMesh.userData.duplicateSourceFile) ||
    null;

  duplicateMesh.isDuplicate = true;
  delete duplicateMesh.asset_file_path;
  delete duplicateMesh.asset_file_pathh;

  duplicateMesh.userData = duplicateMesh.userData || {};
  duplicateMesh.userData.isDuplicate = true;
  delete duplicateMesh.userData.asset_file_path;
  delete duplicateMesh.userData.published;

  if (sourceFile) {
    duplicateMesh.userData.duplicateSourceFile = sourceFile;
    // Keep live file refs for re-duplicate before publish (skinned GLBs need this).
    duplicateMesh.localFile = sourceFile;
    duplicateMesh.originalFile = sourceFile;
  }
}

/**
 * True when the user relocated the object's transform origin (Edit Pivot Point).
 * Those edits must be baked into the published GLB � the original upload file
 * still has the pre-edit hierarchy.
 */
export function isPivotEditedAsset(mesh, groupp = null) {
  return !!(
    groupp?.userData?.pivotEdited ||
    groupp?.pivotEdited ||
    mesh?.userData?.pivotEdited ||
    mesh?.pivotEdited ||
    mesh?.pivot_edited ||
    (Array.isArray(mesh?.pivot_content_matrix) &&
      mesh.pivot_content_matrix.length === 16) ||
    (Array.isArray(groupp?.userData?.pivotContentMatrix) &&
      groupp.userData.pivotContentMatrix.length === 16)
  );
}

/** True when loaded GLB extras already encode a custom pivot (do not re-center). */
export function hierarchyHasPivotEditFlag(root) {
  if (!root) return false;
  let found = false;
  root.traverse?.((obj) => {
    if (found) return;
    if (
      obj.userData?.pivotEdited ||
      obj.userData?.pivot_edited ||
      obj.pivotEdited ||
      obj.pivot_edited
    ) {
      found = true;
    }
  });
  if (
    root.userData?.pivotEdited ||
    root.userData?.pivot_edited ||
    root.pivotEdited ||
    root.pivot_edited
  ) {
    return true;
  }
  return found;
}

/**
 * Resolve which GLB bytes to upload on publish.
 * Skinned/rigged models must use the original upload � GLTFExporter breaks skin joints.
 * Pivot edits must re-export so compensated child transforms are baked into the file.
 */
export async function resolvePublishGlbSource({
  mesh,
  groupp,
  materialModified,
}) {
  const isNormalGlb =
    mesh.glb_type === 'normal' || mesh.glb_type == null || mesh.glb_type === '';
  const isSkinned = hasSkinnedMeshInHierarchy(groupp);
  const isDuplicate = isDuplicateSceneAsset(mesh, groupp);
  const originalFile = findOriginalGlbFile(mesh, groupp);
  const pivotEdited = isPivotEditedAsset(mesh, groupp);

  // Pivot edits live in scene transforms/geometry � never reuse the pre-edit file.
  if (pivotEdited && groupp) {
    try {
      const blob = await groupToGlbBlob(groupp, [], {
        allowSkinned: isSkinned,
      });
      if (blob?.size > 0) {
        return {
          source: blob,
          skipUpload: false,
          pivotBaked: true,
          isSkinned,
        };
      }
    } catch (err) {
      console.error(
        '[resolvePublishGlbSource] pivot-edit GLB export failed:',
        err
      );
      return {
        source: null,
        skipUpload: false,
        error:
          err?.message ||
          'Failed to export pivot-edited model. Try re-uploading, then edit the pivot again.',
      };
    }
  }

  // Library and Sketchfab assets always reference the original hosted URL �
  // never re-export the GLB. The material changes are stored in material_settings JSON only.
  const isLibraryAsset =
    mesh.source === 'library' ||
    groupp?.source === 'library' ||
    mesh.from_library === true ||
    groupp?.from_library === true;
  const isSketchfabAsset =
    mesh.source === 'sketchfab' ||
    groupp?.source === 'sketchfab';

  if (isLibraryAsset || isSketchfabAsset) {
    const existingPath = mesh.asset_file_pathh || mesh.asset_file_path || mesh.url || groupp?.url;
    if (existingPath) {
      return { source: SKIP_GLB_FILE_UPLOAD, skipUpload: true };
    }
  }

  if (
    isDuplicate &&
    isNormalGlb &&
    !isSkinned &&
    groupp &&
    groupp.glb_type !== 'shapes'
  ) {
    try {
      const blob = await groupToGlbBlob(groupp);
      return { source: blob, skipUpload: false, isDuplicate: true };
    } catch (err) {
      console.error(
        '[resolvePublishGlbSource] duplicate GLB export failed:',
        err
      );
      if (originalFile instanceof File) {
        return { source: originalFile, skipUpload: false, isDuplicate: true };
      }
      return {
        source: null,
        skipUpload: false,
        isDuplicate: true,
        error:
          err?.message ||
          'Failed to export duplicate GLB from scene. Try re-uploading the model.',
      };
    }
  }

  if (isSkinned && isNormalGlb) {
    // Bake live child materials into the GLB when edited. Export uses the live
    // hierarchy (no Object3D.clone) so skeleton bone refs stay valid.
    if (materialModified && groupp && groupp.glb_type !== 'shapes') {
      try {
        const blob = await groupToGlbBlob(groupp, [], { allowSkinned: true });
        if (blob?.size > 0) {
          return {
            source: blob,
            skipUpload: false,
            isSkinned: true,
            materialBaked: true,
          };
        }
      } catch (err) {
        console.warn(
          '[resolvePublishGlbSource] skinned material bake failed; keeping original GLB + childOverrides:',
          err
        );
      }
    }
    if (originalFile) {
      return { source: originalFile, skipUpload: false, isSkinned: true };
    }
    const existingPath = mesh.asset_file_pathh || mesh.asset_file_path;
    if (existingPath) {
      return {
        source: SKIP_GLB_FILE_UPLOAD,
        skipUpload: true,
        isSkinned: true,
      };
    }
    return {
      source: null,
      skipUpload: false,
      isSkinned: true,
      error:
        'This rigged model cannot be re-exported. Re-upload the original .glb file, then publish again.',
    };
  }

  const canUseOriginalFile =
    !pivotEdited &&
    !isDuplicate &&
    groupp?.originalFile instanceof File &&
    isNormalGlb &&
    !materialModified &&
    mesh.glb_type !== 'obj' &&
    mesh.glb_type !== 'fbx' &&
    mesh.glb_type !== 'shapes';

  if (canUseOriginalFile) {
    return { source: groupp.originalFile, skipUpload: false };
  }
  if (
    !pivotEdited &&
    originalFile instanceof File &&
    isNormalGlb &&
    !materialModified
  ) {
    return { source: originalFile, skipUpload: false };
  }

  // Shapes are scene-authored — always bake the live materials/maps into a new GLB.
  // Skipping this left publish uploading the empty/original primitive without maps,
  // so textures vanished from the editor after the post-publish LoadContent reload.
  if (groupp && groupp.glb_type === 'shapes') {
    try {
      const blob = await groupToGlbBlob(groupp);
      if (blob?.size > 0) {
        return { source: blob, skipUpload: false, isShape: true };
      }
    } catch (err) {
      console.error(
        '[resolvePublishGlbSource] shape GLB export failed:',
        err
      );
      return {
        source: null,
        skipUpload: false,
        error:
          err?.message ||
          'Failed to export shape with materials. Try publishing again.',
      };
    }
  }

  if (groupp && groupp.glb_type !== 'shapes') {
    try {
      const blob = await groupToGlbBlob(groupp);
      return { source: blob, skipUpload: false };
    } catch (err) {
      console.error('[resolvePublishGlbSource] groupToGlbBlob failed:', err);
      if (originalFile instanceof File) {
        return { source: originalFile, skipUpload: false };
      }
      return {
        source: null,
        skipUpload: false,
        error:
          err?.message ||
          'Failed to export GLB from scene. Try re-uploading the model.',
      };
    }
  }
  return { source: mesh.originalFile ?? null, skipUpload: false };
}

/** GLTFExporter calls clip.clone() � only real THREE.AnimationClip instances are safe. */
function isExportableAnimationClip(clip) {
  return clip instanceof THREE.AnimationClip;
}

function hasMeshContent(object) {
  if (!object) return false;
  if (object.isMesh || object.isSkinnedMesh) return true;
  let found = false;
  object.traverse((o) => {
    if (o.isMesh || o.isSkinnedMesh) found = true;
  });
  return found;
}

/**
 * Editor meshes use a pivot Group > gltf.scene child. Export the inner GLB root so
 * skin joint indices match the node list (avoids GLTFLoader "isBone" on undefined).
 */
export function resolveGltfExportRoot(source) {
  if (!source || typeof source.traverse !== 'function') return source;

  const isMeshWrapper =
    source.deleteType === 'mesh' || source.objType === '3d';

  if (
    !isMeshWrapper ||
    !Array.isArray(source.children) ||
    source.children.length === 0
  ) {
    return source;
  }

  const contentChildren = source.children.filter(
    (child) =>
      hasMeshContent(child) &&
      !child.userData?.isHotspot &&
      child.deleteType !== 'hotspot' &&
      !isNestedGlbAssetPivot(child, source)
  );

  if (contentChildren.length === 1) {
    return contentChildren[0];
  }

  return source;
}

function stripNonExportableObjects(root) {
  const removals = [];
  root.traverse((obj) => {
    if (
      obj.userData?.isHotspot ||
      obj.deleteType === 'hotspot' ||
      obj.isHelper === true ||
      obj.type === 'SkeletonHelper'
    ) {
      removals.push(obj);
    }
  });
  removals.forEach((obj) => obj.parent?.remove(obj));
}

/** Separate uploaded GLB pivots parented under this asset must not be baked into its GLB file. */
function stripNestedGlbAssetPivotsFromExport(exportRoot, hostPivot) {
  if (!exportRoot || !hostPivot) return;
  const removals = [];
  exportRoot.traverse((obj) => {
    if (obj === exportRoot) return;
    if (isNestedGlbAssetPivot(obj, hostPivot)) {
      removals.push(obj);
    }
  });
  removals.forEach((obj) => obj.parent?.remove(obj));
}

function prepareSkinnedMeshesForGltfExport(root) {
  root.updateMatrixWorld(true);

  root.traverse((obj) => {
    if (obj.isBone || obj.isSkinnedMesh) {
      obj.visible = true;
      obj.matrixAutoUpdate = true;
    }
  });

  const nodesInExport = new Set();
  root.traverse((o) => nodesInExport.add(o));

  root.traverse((obj) => {
    if (!obj.isSkinnedMesh || !obj.skeleton) return;

    const bones = obj.skeleton.bones || [];
    const missingBone = bones.some((bone) => !nodesInExport.has(bone));
    if (missingBone) {
      console.warn(
        '[groupToGlbBlob] Skinned mesh has bones outside export root; mesh may need re-upload',
        obj.name
      );
      return;
    }

    bones.forEach((bone) => bone.updateMatrixWorld(true));
    obj.updateMatrixWorld(true);
  });
}

function collectAnimationClips(root, extraAnimations = []) {
  const clipsToExport = [];
  const seen = new Set();

  const addClip = (clip) => {
    if (!isExportableAnimationClip(clip)) return;
    if (seen.has(clip)) return;
    seen.add(clip);
    clipsToExport.push(clip);
  };

  if (Array.isArray(extraAnimations)) {
    extraAnimations.forEach(addClip);
  }

  if (Array.isArray(root.animations)) {
    root.animations.forEach(addClip);
  }

  root.traverse((obj) => {
    if (Array.isArray(obj.animations)) {
      obj.animations.forEach(addClip);
    }
    const clips = obj.userData?.animationClips;
    if (Array.isArray(clips)) {
      clips.forEach(addClip);
    }
  });

  return clipsToExport;
}

const GLTF_PRESERVE_MATERIAL_TYPES = new Set([
  'MeshStandardMaterial',
  'MeshPhysicalMaterial',
  'MeshBasicMaterial',
  'MeshLambertMaterial',
  'MeshPhongMaterial',
  'MeshToonMaterial',
  'MeshMatcapMaterial',
]);

function copyScalarMaterialProps(from, to) {
  const keys = [
    'name',
    'metalness',
    'roughness',
    'emissiveIntensity',
    'aoMapIntensity',
    'bumpScale',
    'displacementScale',
    'displacementBias',
    'lightMapIntensity',
    'envMapIntensity',
    'shininess',
    'clearcoat',
    'clearcoatRoughness',
    'transmission',
    'thickness',
    'ior',
    'reflectivity',
    'refractionRatio',
    'wireframe',
    'wireframeLinewidth',
    'flatShading',
    'vertexColors',
    'alphaTest',
    'alphaHash',
    'transparent',
    'opacity',
    'depthWrite',
    'depthTest',
    'side',
    'blending',
  ];
  keys.forEach((key) => {
    if (key in from) {
      to[key] = from[key];
    }
  });
  if (from.normalScale && to.normalScale) {
    to.normalScale = from.normalScale.clone?.() ?? from.normalScale;
  }
}

function convertToExportableMaterial(mat) {
  if (!mat) return mat;

  if (GLTF_PRESERVE_MATERIAL_TYPES.has(mat.type)) {
    const cloned = mat.clone();
    copyScalarMaterialProps(mat, cloned);
    return cloned;
  }

  const usePhysical =
    mat.clearcoat !== undefined ||
    mat.transmission !== undefined ||
    mat.ior !== undefined;

  const baseProps = {
    name: mat.name,
    color: mat.color?.clone?.() ?? new THREE.Color(0xffffff),
    map: isNonExportableTexture(mat.map) ? null : mat.map ?? null,
    normalMap: isNonExportableTexture(mat.normalMap) ? null : mat.normalMap ?? null,
    roughnessMap: isNonExportableTexture(mat.roughnessMap)
      ? null
      : mat.roughnessMap ?? null,
    metalnessMap: isNonExportableTexture(mat.metalnessMap)
      ? null
      : mat.metalnessMap ?? null,
    aoMap: isNonExportableTexture(mat.aoMap) ? null : mat.aoMap ?? null,
    emissiveMap: isNonExportableTexture(mat.emissiveMap)
      ? null
      : mat.emissiveMap ?? null,
    alphaMap: isNonExportableTexture(mat.alphaMap) ? null : mat.alphaMap ?? null,
    bumpMap: isNonExportableTexture(mat.bumpMap) ? null : mat.bumpMap ?? null,
    displacementMap: isNonExportableTexture(mat.displacementMap)
      ? null
      : mat.displacementMap ?? null,
    lightMap: isNonExportableTexture(mat.lightMap) ? null : mat.lightMap ?? null,
    envMap: isNonExportableTexture(mat.envMap) ? null : mat.envMap ?? null,
    emissive: mat.emissive?.clone?.() ?? new THREE.Color(0x000000),
    transparent: !!mat.transparent || (typeof mat.opacity === 'number' && mat.opacity < 1),
    opacity: typeof mat.opacity === 'number' ? mat.opacity : 1,
    side: mat.side ?? THREE.FrontSide,
    alphaTest: mat.alphaTest ?? 0,
    depthWrite: mat.depthWrite !== false,
    wireframe: mat.wireframe === true,
    blending: mat.blending ?? THREE.NormalBlending,
  };

  const standard = usePhysical
    ? new THREE.MeshPhysicalMaterial(baseProps)
    : new THREE.MeshStandardMaterial(baseProps);

  if (mat.metalness !== undefined) standard.metalness = mat.metalness;
  if (mat.roughness !== undefined) standard.roughness = mat.roughness;
  if (mat.emissiveIntensity !== undefined) {
    standard.emissiveIntensity = mat.emissiveIntensity;
  }
  if (mat.aoMapIntensity !== undefined) {
    standard.aoMapIntensity = mat.aoMapIntensity;
  }
  if (mat.normalScale) {
    standard.normalScale = mat.normalScale.clone?.() ?? mat.normalScale;
  }
  if (mat.bumpScale !== undefined) standard.bumpScale = mat.bumpScale;
  if (mat.displacementScale !== undefined) {
    standard.displacementScale = mat.displacementScale;
  }
  if (mat.displacementBias !== undefined) {
    standard.displacementBias = mat.displacementBias;
  }
  if (mat.lightMapIntensity !== undefined) {
    standard.lightMapIntensity = mat.lightMapIntensity;
  }
  if (mat.envMapIntensity !== undefined) {
    standard.envMapIntensity = mat.envMapIntensity;
  }

  copyScalarMaterialProps(mat, standard);
  return standard;
}

/** Clone textures so GLTF export flipY/colorSpace changes never mutate the live editor scene. */
function cloneTextureForExport(tex, key) {
  if (!tex?.isTexture) return null;
  // Video textures are referenced via mesh.video_texture � never embed in GLB.
  if (isNonExportableTexture(tex)) return null;
  const texClone = tex.clone();
  texClone.flipY = false;
  if (key === 'map' || key === 'emissiveMap' || key === 'matcap') {
    texClone.colorSpace = THREE.SRGBColorSpace;
  } else {
    texClone.colorSpace = THREE.LinearSRGBColorSpace;
  }
  // Prefer JPEG for non-alpha maps � PNG encode of large bump maps is the publish hang.
  const jpegSafeKeys = new Set([
    'bumpMap',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'aoMap',
    'lightMap',
    'displacementMap',
    'specularMap',
  ]);
  if (jpegSafeKeys.has(key)) {
    texClone.userData = { ...(texClone.userData || {}), mimeType: 'image/jpeg' };
  }
  if (texClone.image) texClone.needsUpdate = true;
  return texClone;
}

/**
 * GLTFExporter only writes alpha / BLEND when transparent is true (or alphaTest).
 * Also treat alphaMap as needing BLEND (opacity may still be 1).
 */
function ensureGltfAlphaExportState(mat) {
  if (!mat) return;

  const opacity = typeof mat.opacity === 'number' ? mat.opacity : 1;
  const transparent = !!mat.transparent;
  const alphaTest = typeof mat.alphaTest === 'number' ? mat.alphaTest : 0;
  const hasAlphaMap = !!mat.alphaMap?.isTexture;
  const needsBlend =
    opacity < 1 - 1e-6 || transparent || alphaTest > 0 || hasAlphaMap;

  if (needsBlend) {
    mat.transparent = true;
    mat.opacity = opacity;
    if (
      (opacity < 1 - 1e-6 || hasAlphaMap) &&
      mat.depthWrite !== undefined
    ) {
      mat.depthWrite = false;
    }
    if (alphaTest > 0 && mat.alphaTest !== undefined) {
      mat.alphaTest = alphaTest;
    }
    mat.userData = mat.userData || {};
    mat.userData.editorNeedsTransparency = true;
  } else {
    mat.transparent = false;
    mat.opacity = opacity;
    if (mat.depthWrite === undefined) {
      mat.depthWrite = true;
    }
    if (mat.userData) delete mat.userData.editorNeedsTransparency;
  }

  if (mat.color?.isColor && opacity < 1 - 1e-6) {
    mat.color.set(mat.color.r, mat.color.g, mat.color.b);
  }

  mat.needsUpdate = true;
}

function prepareMaterialCloneForExport(mat, preparedBySourceUuid) {
  if (!mat) return mat;

  const cache = preparedBySourceUuid || null;
  if (cache?.has(mat.uuid)) {
    return cache.get(mat.uuid);
  }

  const sourceUd = mat.userData || {};
  const backups = sourceUd[MAP_BACKUP_USERDATA_KEY] || {};
  const mapVisibility = { ...(sourceUd[MAP_VISIBILITY_USERDATA_KEY] || {}) };

  TEXTURE_KEYS.forEach((key) => {
    const texture = mat[key] || backups[key];
    if (texture?.isTexture) {
      if (mapVisibility[key] === undefined) {
        mapVisibility[key] = !!mat[key];
      }
    }
  });

  const prepared = convertToExportableMaterial(mat);
  prepared.visible = true;
  prepared.needsUpdate = true;

  // Restore textures from visibility backups when the live slot was cleared
  // but the map is still meant to publish (or is still on the material).
  TEXTURE_KEYS.forEach((key) => {
    const live = mat[key];
    const backup = backups[key];
    const shouldExport =
      mapVisibility[key] !== false &&
      ((live && live.isTexture) || (backup && backup.isTexture));
    if (shouldExport && !prepared[key]?.isTexture) {
      prepared[key] = live?.isTexture ? live : backup;
    }
    if (prepared[key]?.isTexture) {
      prepared[key] = cloneTextureForExport(prepared[key], key);
    }
    // Defensive: mat.clone() paths can still carry VideoTexture through.
    if (isNonExportableTexture(prepared[key])) {
      prepared[key] = null;
    }
  });

  // After maps are on the clone (esp. alphaMap), set GLTF BLEND flags.
  // Prefer live editor transparent / alphaMap over stale Render View snapshots.
  if (mat.transparent || mat.alphaMap || prepared.alphaMap) {
    prepared.transparent = true;
    if (prepared.depthWrite !== undefined) prepared.depthWrite = false;
  }
  ensureGltfAlphaExportState(prepared);

  prepared.userData = prepared.userData || {};
  delete prepared.userData.originalMaterialProps;
  // Texture instances are not JSON-serializable — strip backups or GLTFExporter
  // silently drops ALL material extras (including mapPreviewUrls for alphaMap).
  delete prepared.userData[MAP_BACKUP_USERDATA_KEY];
  prepared.userData[MAP_VISIBILITY_USERDATA_KEY] = mapVisibility;
  // Remember editor sharing so loaders/overrides can re-link siblings.
  prepared.userData.editorSharedMaterialId = mat.uuid;
  // glTF has no wireframe flag — stash in extras so baked GLBs restore it on load.
  if (mat.wireframe === true) {
    prepared.userData.editorWireframe = true;
  } else {
    delete prepared.userData.editorWireframe;
  }

  // Persist intentional transparency so reload keeps BLEND even before alphaMap restores.
  if (
    mat.transparent ||
    prepared.transparent ||
    mat.alphaMap ||
    prepared.alphaMap ||
    (typeof mat.opacity === 'number' && mat.opacity < 1 - 1e-6)
  ) {
    prepared.userData.editorNeedsTransparency = true;
  } else {
    delete prepared.userData.editorNeedsTransparency;
  }

  // Map intensity / scale are not reliable glTF fields (esp. lightMapIntensity).
  // Persist on material extras so reconstruct matches the editor.
  const mapScalars = snapshotMapScalars(mat);
  if (mapScalars) {
    prepared.userData[EDITOR_MAP_SCALARS_KEY] = mapScalars;
  } else {
    delete prepared.userData[EDITOR_MAP_SCALARS_KEY];
  }

  // Keep durable / restorable preview URLs so non-glTF maps (alphaMap, ...)
  // round-trip via material extras. Drop ephemeral blob: URLs.
  const srcPreviews = sourceUd[MAP_PREVIEW_URLS_USERDATA_KEY] || {};
  const nextPreviews = {};
  Object.entries(srcPreviews).forEach(([key, url]) => {
    if (
      isRestorableTextureUrl(url) &&
      (isPublishableTextureUrl(url) || String(url).startsWith('data:'))
    ) {
      nextPreviews[key] = url;
    }
  });
  if (Object.keys(nextPreviews).length) {
    prepared.userData[MAP_PREVIEW_URLS_USERDATA_KEY] = nextPreviews;
  } else {
    delete prepared.userData[MAP_PREVIEW_URLS_USERDATA_KEY];
  }

  if (cache) cache.set(mat.uuid, prepared);
  return prepared;
}

/**
 * Prepare materials for GLTFExporter while preserving shared instances.
 * Cloning per-mesh broke "edit one child ? all siblings with same material update"
 * after publish/reload.
 */
function prepareMaterialsForGltfExport(root) {
  const preparedBySourceUuid = new Map();

  root.traverse((child) => {
    if ((!child.isMesh && !child.isSkinnedMesh) || !child.material) return;

    if (Array.isArray(child.material)) {
      child.material = child.material.map((mat) =>
        prepareMaterialCloneForExport(mat, preparedBySourceUuid)
      );
    } else {
      child.material = prepareMaterialCloneForExport(
        child.material,
        preparedBySourceUuid
      );
    }
  });
}

function waitForTextureImage(tex) {
  return new Promise((resolve) => {
    if (!tex?.isTexture) {
      resolve();
      return;
    }

    const done = () => resolve();

    const waitForImage = (img) => {
      if (!img) {
        requestAnimationFrame(done);
        return;
      }
      if (typeof Image !== 'undefined' && img instanceof Image) {
        if (img.complete) {
          done();
          return;
        }
        img.onload = done;
        img.onerror = done;
        return;
      }
      if (typeof HTMLCanvasElement !== 'undefined' && img instanceof HTMLCanvasElement) {
        done();
        return;
      }
      if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
        done();
        return;
      }
      if (typeof img === 'string') {
        done();
        return;
      }
      requestAnimationFrame(done);
    };

    waitForImage(tex.image);
  });
}

function waitForPendingTextures(root) {
  const waits = [];

  root.traverse((child) => {
    if ((!child.isMesh && !child.isSkinnedMesh) || !child.material) return;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((mat) => {
      TEXTURE_KEYS.forEach((key) => {
        if (mat?.[key]) waits.push(waitForTextureImage(mat[key]));
      });
    });
  });

  return waits.length ? Promise.all(waits) : Promise.resolve();
}

/** After publish, keep re-export source aligned with the last uploaded GLB bytes. */
export function cacheGlbFileOnSceneObject(groupp, blobOrFile, fileName, options = {}) {
  if (!groupp || !blobOrFile) return;
  // Default: never replace rigged source (unsafe clone exports). Allow when
  // materials were baked via the live skinned export path.
  if (hasSkinnedMeshInHierarchy(groupp) && !options.allowSkinned) return;
  const file =
    blobOrFile instanceof File
      ? blobOrFile
      : new File([blobOrFile], fileName || 'model.glb', {
          type: 'model/gltf-binary',
          lastModified: Date.now(),
        });
  groupp.originalFile = file;
  groupp.localFile = file;
}

function disposeClonedHierarchy(root) {
  root.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      const materials = Array.isArray(obj.material)
        ? obj.material
        : [obj.material];
      materials.forEach((mat) => {
        if (!mat) return;
        TEXTURE_KEYS.forEach((key) => {
          const tex = mat[key];
          if (tex?.isTexture) tex.dispose();
        });
        mat.dispose();
      });
    }
  });
}

/** AnimationMixer in userData creates circular refs � strip before Object3D.clone(). */
const ANIMATION_RUNTIME_KEYS = ['mixer', 'animationActions'];

function stripAnimationRuntimeFromUserData(root) {
  const backup = new Map();
  root.traverse((obj) => {
    if (!obj.userData) return;
    const saved = {};
    let changed = false;
    ANIMATION_RUNTIME_KEYS.forEach((key) => {
      if (obj.userData[key] !== undefined) {
        saved[key] = obj.userData[key];
        delete obj.userData[key];
        changed = true;
      }
    });
    if (changed) backup.set(obj, saved);
  });
  return backup;
}

function restoreAnimationRuntimeToUserData(backup) {
  backup.forEach((saved, obj) => {
    if (!obj.userData) obj.userData = {};
    Object.assign(obj.userData, saved);
  });
}

function cloneGroupForExport(source) {
  const backup = stripAnimationRuntimeFromUserData(source);
  let exportRoot;
  try {
    exportRoot = source.clone(true);
  } finally {
    restoreAnimationRuntimeToUserData(backup);
  }
  // Object3D.clone shares geometry/materials � deep-clone so disposeRoot
  // (and any bake) cannot mutate or destroy the live scene assets.
  exportRoot.traverse((obj) => {
    if (obj.geometry) {
      obj.geometry = obj.geometry.clone();
    }
  });
  return exportRoot;
}

/**
 * Fold `root`'s local matrix into its children (or mesh geometry) so the root
 * can be reset to identity without losing pivot-edit offsets.
 */
function bakeLocalTransformIntoDescendants(root) {
  if (!root) return;

  root.updateMatrix();
  const local = root.matrix.clone();
  const identity = new THREE.Matrix4();
  if (local.equals(identity)) return;

  const children = root.children?.slice?.() || [];
  if (children.length > 0) {
    children.forEach((child) => {
      child.applyMatrix4(local);
    });
  } else if (root.isSkinnedMesh) {
    // Avoid geometry.bindPose breakage � keep object matrix; caller must not
    // blindly zero a leaf skinned export root without rebinding.
    return;
  } else if ((root.isMesh || root.isSkinnedMesh) && root.geometry) {
    root.geometry.applyMatrix4(local);
    root.geometry.computeBoundingBox?.();
    root.geometry.computeBoundingSphere?.();
  }

  root.position.set(0, 0, 0);
  root.quaternion.identity();
  root.scale.set(1, 1, 1);
  root.updateMatrix();
}

/**
 * Asset pivot transform is stored in JSON. Content under the pivot may hold
 * pivot-edit compensation in its local matrix � bake that before identity reset.
 */
function prepareExportRootTransform(exportRoot, hostPivot) {
  if (!exportRoot) return;

  // When we export the inner glTF root (not the editor pivot wrapper), its local
  // matrix may carry pivot-edit compensation. Bake it so identity reset is safe.
  // (exportRoot is often a clone, so do not use hostPivot.children.includes.)
  const liveContent = hostPivot ? resolveGltfExportRoot(hostPivot) : null;
  const exportingInnerContent =
    !!hostPivot && !!liveContent && liveContent !== hostPivot;

  if (exportingInnerContent) {
    bakeLocalTransformIntoDescendants(exportRoot);
  }

  // Persist flag in GLB extras so reload can skip geometric re-centering.
  if (isPivotEditedAsset(hostPivot, hostPivot) || isPivotEditedAsset(exportRoot)) {
    exportRoot.userData = exportRoot.userData || {};
    exportRoot.userData.pivotEdited = true;
    exportRoot.userData.pivot_edited = true;
  }

  // Scene position/rotation/scale are stored in asset JSON on reload (not in the GLB).
  exportRoot.position.set(0, 0, 0);
  exportRoot.quaternion.identity();
  exportRoot.scale.set(1, 1, 1);
  exportRoot.updateMatrixWorld(true);
}

/**
 * Converts a scene Group (pivot + GLB child) into a GLB Blob for publish.
 * Uses a clone so the live scene is not mutated. Embeds textures when possible.
 *
 * @param {object} [options]
 * @param {boolean} [options.allowSkinned] - Export live skinned hierarchy (no
 *   Object3D.clone � cloning breaks skeleton bone refs). Used when baking
 *   child material edits into animation GLBs.
 */
export async function groupToGlbBlob(group, animations = [], options = {}) {
  if (!group) {
    throw new Error('groupToGlbBlob: group is required');
  }

  // Strip Render View viewport preview so published GLB keeps original materials.
  return withRenderViewStrippedAsync(group, () =>
    groupToGlbBlobUnwrapped(group, animations, options)
  );
}

async function groupToGlbBlobUnwrapped(group, animations = [], options = {}) {
  const { allowSkinned = false } = options;
  const exportSource = resolveGltfExportRoot(group);
  const isSkinned = hasSkinnedMeshInHierarchy(exportSource);

  if (isSkinned && !allowSkinned) {
    throw new Error(
      'Skinned/rigged GLB cannot be re-exported safely. Use the original uploaded file.'
    );
  }

  // Skinned: export the live tree with temporary material swaps (clone breaks skins).
  if (isSkinned && allowSkinned) {
    return exportSkinnedGroupWithBakedMaterials(
      exportSource,
      group,
      animations
    );
  }

  const exportRoot = cloneGroupForExport(exportSource);

  stripNestedGlbAssetPivotsFromExport(exportRoot, group);
  stripNonExportableObjects(exportRoot);

  exportRoot.traverse((obj) => {
    ANIMATION_RUNTIME_KEYS.forEach((key) => {
      if (obj.userData?.[key] !== undefined) delete obj.userData[key];
    });
    if (obj.userData) {
      delete obj.userData.threeMaterialEdited;
    }
  });

  // Preserve pivot-edit offsets inside the file, then clear root transform
  // (root placement comes from position_x/y/z on reload).
  prepareExportRootTransform(exportRoot, group);

  prepareSkinnedMeshesForGltfExport(exportRoot);
  prepareMaterialsForGltfExport(exportRoot);
  await waitForPendingTextures(exportRoot);
  // alphaMap / specularMap / envMap / … are not written by GLTFExporter —
  // encode them into material extras so reload restores via TextureLoader.
  await embedNonGltfMapUrlsInMaterials(exportRoot);

  const clipsToExport = collectAnimationClips(exportSource, animations);

  return runGltfBinaryExport(exportRoot, clipsToExport, {
    disposeRoot: true,
  });
}

/**
 * Temporarily detach non-exportable children from a live hierarchy, then restore.
 * (Cannot use Object3D.clone on skinned models � bone refs break.)
 */
function detachNonExportableFromLiveRoot(exportSource, hostPivot) {
  const detached = [];
  const toDetach = [];
  exportSource.traverse((obj) => {
    if (obj === exportSource) return;
    if (
      obj.userData?.isHotspot ||
      obj.deleteType === 'hotspot' ||
      obj.isHelper === true ||
      obj.type === 'SkeletonHelper' ||
      isNestedGlbAssetPivot(obj, hostPivot)
    ) {
      toDetach.push(obj);
    }
  });
  toDetach.forEach((obj) => {
    if (!obj.parent) return;
    detached.push({
      obj,
      parent: obj.parent,
      index: obj.parent.children.indexOf(obj),
    });
    obj.parent.remove(obj);
  });
  return detached;
}

function reattachDetachedChildren(detached) {
  detached.forEach(({ obj, parent }) => {
    if (parent && obj) parent.add(obj);
  });
}

/**
 * Bake current child materials into a skinned GLB without cloning the skeleton.
 * Temporarily swaps materials for export, then restores the live scene.
 */
async function exportSkinnedGroupWithBakedMaterials(
  exportSource,
  hostPivot,
  animations = []
) {
  const materialBackup = [];
  exportSource.traverse((child) => {
    if ((!child.isMesh && !child.isSkinnedMesh) || !child.material) return;
    materialBackup.push({
      obj: child,
      material: child.material,
    });
  });

  const transformBackup = {
    position: exportSource.position.clone(),
    quaternion: exportSource.quaternion.clone(),
    scale: exportSource.scale.clone(),
  };

  const runtimeBackup = stripAnimationRuntimeFromUserData(exportSource);
  const detached = detachNonExportableFromLiveRoot(exportSource, hostPivot);
  const preparedMats = [];
  let bakedLocal = null;

  try {
    prepareMaterialsForGltfExport(exportSource);
    exportSource.traverse((child) => {
      if ((!child.isMesh && !child.isSkinnedMesh) || !child.material) return;
      const mats = Array.isArray(child.material)
        ? child.material
        : [child.material];
      mats.forEach((m) => {
        if (m && !materialBackup.some((b) => b.material === m)) {
          preparedMats.push(m);
        }
      });
    });
    await waitForPendingTextures(exportSource);
    await embedNonGltfMapUrlsInMaterials(exportSource);

    // Same as static export: keep pivot-edit offsets when clearing root transform.
    const liveContent = hostPivot ? resolveGltfExportRoot(hostPivot) : null;
    const exportingInnerContent =
      !!hostPivot && !!liveContent && liveContent !== hostPivot;

    if (exportingInnerContent) {
      exportSource.updateMatrix();
      const local = exportSource.matrix.clone();
      const identity = new THREE.Matrix4();
      if (!local.equals(identity)) {
        bakedLocal = local;
        exportSource.children.slice().forEach((child) => {
          child.applyMatrix4(bakedLocal);
        });
      }
    }

    exportSource.position.set(0, 0, 0);
    exportSource.quaternion.identity();
    exportSource.scale.set(1, 1, 1);
    exportSource.updateMatrixWorld(true);
    prepareSkinnedMeshesForGltfExport(exportSource);

    const missingSkin = (() => {
      let bad = false;
      const nodes = new Set();
      exportSource.traverse((o) => nodes.add(o));
      exportSource.traverse((obj) => {
        if (!obj.isSkinnedMesh || !obj.skeleton) return;
        if ((obj.skeleton.bones || []).some((b) => !nodes.has(b))) bad = true;
      });
      return bad;
    })();
    if (missingSkin) {
      throw new Error(
        'Skinned mesh bones are outside the export root; cannot bake materials.'
      );
    }

    const clipsToExport = collectAnimationClips(exportSource, animations);
    return await runGltfBinaryExport(exportSource, clipsToExport, {
      disposeRoot: false,
    });
  } finally {
    materialBackup.forEach(({ obj, material }) => {
      obj.material = material;
    });
    preparedMats.forEach((mat) => {
      try {
        TEXTURE_KEYS.forEach((key) => {
          const tex = mat?.[key];
          if (tex?.isTexture && tex.dispose) tex.dispose();
        });
        mat.dispose?.();
      } catch {
        /* ignore */
      }
    });
    exportSource.position.copy(transformBackup.position);
    exportSource.quaternion.copy(transformBackup.quaternion);
    exportSource.scale.copy(transformBackup.scale);
    if (bakedLocal) {
      const inverse = bakedLocal.clone().invert();
      exportSource.children.slice().forEach((child) => {
        child.applyMatrix4(inverse);
      });
    }
    exportSource.updateMatrixWorld(true);
    reattachDetachedChildren(detached);
    restoreAnimationRuntimeToUserData(runtimeBackup);
  }
}

function runGltfBinaryExport(exportRoot, clipsToExport, { disposeRoot }) {
  const exportOptions = {
    binary: true,
    embedImages: true,
    onlyVisible: false,
    truncateDrawRange: false,
    maxTextureSize: 2048,
  };
  if (clipsToExport.length > 0) {
    exportOptions.animations = clipsToExport;
  }

  return new Promise((resolve, reject) => {
    const exporter = new GLTFExporter();
    exporter.parse(
      exportRoot,
      (result) => {
        if (disposeRoot) disposeClonedHierarchy(exportRoot);
        if (!(result instanceof ArrayBuffer)) {
          reject(new Error('GLTFExporter did not return ArrayBuffer'));
          return;
        }
        resolve(new Blob([result], { type: 'model/gltf-binary' }));
      },
      (err) => {
        if (disposeRoot) disposeClonedHierarchy(exportRoot);
        reject(err);
      },
      exportOptions
    );
  });
}
